import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import {
  Box,
  Button,
  Modal,
  Tooltip,
  Typography
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { capitalize } from "lodash";
import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { deleteUser, fetchPlanGroupsList, fetchRolesList, fetchUsers, updateUser } from "../../../store/features/planManagement/planManagement";
import { fallbackFormatter } from "../../../utils/helpers/formatter";
import { numberColFilterParams } from "../../../utils/helpers/utility_helpers";
import { onFilterChanged } from "../../../utils/reactTable/callbacks/callbacks";
import TooltipRenderer from "../../../utils/reactTable/tooltipRenderer/TooltipRenderer";
import MultiSelect from "../../common/filters/Select/Select";
import TableRowCount from "../../common/tableRowCount/TableRowCount";
import useTableRowCount from "../../common/tableRowCount/useTableRowCount";
import { PRICING_TEAM, SUPER_USER } from "../exceptionConfig/ExceptionConfig";

import styles from "./PlanManagement.module.scss";

// Roles and plan groups will be fetched from API

// Utility function for formatting UTC time
const utcTimeFormatter = ({ value }) => {
  if (!value) return "";
  return moment.utc(value).format("YYYY-MM-DD HH:mm:ss");
};

export default function PlanManagement() {
  const gridRef = useRef();
  const dispatch = useDispatch();

  // Get user permissions from Redux store
  const { role_details } = useSelector((state) => state.global.userPermissions || {});
  // Check if user is a superuser using the proper constant
  const isRowSelectable = !!role_details?.[SUPER_USER] || !!role_details?.[PRICING_TEAM] || false;

  const [
    rowCount,
    selectedRowCount,
    onTableModelUpdate,
    onTableSelectChange,
  ] = useTableRowCount();

  const [selectedRows, setSelectedRows] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editUserData, setEditUserData] = useState(null);
  const [isEditDataValid, setIsEditDataValid] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [planGroupDisabled, setPlanGroupDisabled] = useState(false);

  // Get data from Redux store
  const { users, rolesList, planGroupsList, loading } = useSelector((state) => state.planManagement);

  const columnDef = [
    {
      field: "",
      headerName: "",
      filter: false,
      checkboxSelection: () => isRowSelectable,
      headerCheckboxSelection: () => isRowSelectable,
      headerCheckboxSelectionFilteredOnly: true,
      width: 50,
      valueFormatter: () => "",
      pinned: true,
      lockPinned: true,
    },
    {
      field: "user_id",
      headerName: "User ID",
      flex: 1,
      minWidth: 100,
      filterParams: numberColFilterParams,
    },
    {
      field: "email_address",
      headerName: "Email Address",
      flex: 2,
      minWidth: 180,
    },
    {
      field: "username",
      headerName: "User Name",
      flex: 1.5,
      minWidth: 150,
    },
    {
      field: "region",
      headerName: "Region",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "role_name",
      headerName: "Role",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "plan_group",
      headerName: "Plan Group Tagged",
      flex: 2,
      minWidth: 200,
    },
  ].map((item) => ({
    tooltipField: item.field,
    tooltipComponent: TooltipRenderer,
    ...item,
  }));

  useEffect(() => {
    // Fetch users, roles, and plan groups data from API
    dispatch(fetchUsers());
    dispatch(fetchRolesList());
    dispatch(fetchPlanGroupsList());
  }, [dispatch]);



  const onRefreshClick = () => {
    // Refresh data from API
    dispatch(fetchUsers());
  };

  const onMarketSelect = useCallback(
    (event) => {
      onTableSelectChange(event);
      setSelectedRows(gridRef?.current?.api?.getSelectedNodes() || []);
    },
    [onTableSelectChange]
  );

  const processCellForClipboard = useCallback((params) => {
    const col = params.column.getColId();
    if (["created_at", "updated_at"].includes(col))
      return utcTimeFormatter(params);
    return params.value;
  }, []);

  // Handle edit button click
  const onEditClick = () => {
    if (selectedRows.length !== 1) return;

    // Format the data for editing
    const userData = selectedRows[0].data;

    setEditMode(true);
    setEditUserData({
      ...userData,
      // Ensure we have the proper fields for editing
      userId: userData.user_id,
      role: userData.role_name || '',
      planGroupTagged: Array.isArray(userData.plan_group) ? userData.plan_group.join(', ') :
        (userData.plan_group === null || userData.plan_group === undefined) ? '' : userData.plan_group
    });
  };

  // Handle save button click
  const onSaveClick = async () => {
    if (!isEditDataValid) return;

    // Get current user ID for the API call
    const userId = editUserData.userId || editUserData.user_id;

    // Extract plan groups from the comma-separated string
    const planGroups = editUserData.planGroupTagged ?
      editUserData.planGroupTagged.split(', ').filter(group => group.trim() !== '') : [];

    // Create payload for the API
    const userData = {
      role: editUserData.role.toLowerCase(),
      planGroup: planGroups
    };

    // Get current user ID for the updated_by field
    // This would typically come from the logged-in user's info
    const currentUserId = localStorage.userId;

    // Dispatch action to update the user data
    await dispatch(updateUser(userId, userData, currentUserId));

    // Exit edit mode and clear edit data
    setEditMode(false);
    setEditUserData(null);

    // Deselect row after saving
    gridRef.current.api.deselectAll();
    setSelectedRows([]);
    onRefreshClick();
  };

  // Handle cancel button click
  const onCancelClick = () => {
    setEditMode(false);
    setEditUserData(null);
  };

  const onDeleteClick = () => {
    setDeleteModalOpen(true);
  };

  const onDeleteModalClose = () => {
    setDeleteModalOpen(false);
  };

  const onDeleteUserCancel = () => {
    setDeleteModalOpen(false);
  };

  const onDeleteUserSubmit = async () => {
    // Get current user ID for the updated_by parameter
    const currentUserId = localStorage.userId;

    // Dispatch action to delete the user
    await dispatch(deleteUser(selectedRows[0].data.user_id, currentUserId));

    // Close the modal and clear selection
    setDeleteModalOpen(false);
    setSelectedRows([]);
    gridRef.current.api.deselectAll();
    onRefreshClick();
  };

  // Handle role change
  const handleRoleChange = (data) => {
    const newRole = data.selectedItems[0].value;
    setEditUserData(prev => ({
      ...prev,
      role: newRole,
      // Clear plan group tagged if role is not Merchant
      planGroupTagged: newRole.toLowerCase() !== 'merchant' ? '' : prev.planGroupTagged
    }));

    // If the role is not Merchant, disable the dropdown
    setPlanGroupDisabled(newRole.toLowerCase() !== 'merchant');
  };

  // Handle plan group change
  const handlePlanGroupChange = (data) => {
    const selectedGroups = data.selectedItems.map(item => item.value);
    setEditUserData(prev => ({
      ...prev,
      planGroupTagged: selectedGroups.join(', ')
    }));
  };

  const getActionButtons = () => {
    // No need to check for superuser here as the checkbox selection already does that
    if (editMode) {
      return (
        <>
          <Tooltip title="Save changes">
            <Button
              variant="contained"
              color="primary"
              onClick={onSaveClick}
            >
              <SaveOutlinedIcon fontSize="small" />
            </Button>
          </Tooltip>
          <Button
            variant="outlined"
            onClick={onCancelClick}
          >
            Cancel
          </Button>
        </>
      );
    } else if (selectedRows.length === 1) {
      const selectedUser = selectedRows[0].data;
      return (
        <>
          <Tooltip title="Edit selected user">
            <Button
              variant="contained"
              color="primary"
              onClick={onEditClick}
            >
              <EditOutlinedIcon fontSize="small" />
            </Button>
          </Tooltip>
          <Tooltip title="Delete selected user">
            <Button
              variant="contained"
              color="error"
              onClick={onDeleteClick}
            >
              <DeleteOutlinedIcon fontSize="small" />
            </Button>
          </Tooltip>
        </>
      );
    }
    return null;
  };

  // Format plan_group for display in the grid
  const formattedUsers = users?.map(user => ({
    ...user,
    plan_group: Array.isArray(user.plan_group) ? user.plan_group.join(', ') :
      (user.plan_group === null || user.plan_group === undefined) ? '' : user.plan_group
  })) || [];

  return (
    <div className={`${styles.container}`}>
      <div
        className={`${styles["page-header"]} align-center justify-space-between`}
      >
        <div className={`${styles["page-title"]}`}>User Management</div>
        <div className="button-group-flex">
          <Tooltip title="Refresh data">
            <Button variant="contained" onClick={onRefreshClick}>
              <RefreshOutlinedIcon fontSize="small" />
            </Button>
          </Tooltip>
          {getActionButtons()}
        </div>
      </div>
      <div
        style={{ height: "calc(100vh - 260px)" }}
        className="ag-theme-alpine"
      >
        <AgGridReact
          ref={gridRef}
          rowData={formattedUsers}
          rowSelection="single"
          onSelectionChanged={onMarketSelect}
          defaultColDef={{
            sortable: true,
            resizable: true,
            filter: "agMultiColumnFilter",
            floatingFilter: true,
            menuTabs: ["filterMenuTab", "generalMenuTab"],
            showDisabledCheckboxes: true,
            valueFormatter: fallbackFormatter,
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
          }}
          columnDefs={columnDef}
          onFilterChanged={onFilterChanged}
          suppressClickEdit
          onModelUpdated={onTableModelUpdate}
          enableRangeSelection
          suppressRowClickSelection
          processCellForClipboard={processCellForClipboard}
          tooltipShowDelay={500}
        />
      </div>
      <TableRowCount count={rowCount} selected={selectedRowCount} />

      {/* Edit User Modal */}
      <Modal
        open={editMode}
        onClose={onCancelClick}
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 600,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 1,
        }}>
          <h4 style={{ marginTop: 0 }}>Edit User</h4>

          {editUserData && (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1">Role <span style={{ color: 'red' }}>*</span></Typography>
                <MultiSelect
                  initialData={rolesList}
                  selectedOptions={[{ value: editUserData.role.toLowerCase(), label: capitalize(editUserData.role) }]}
                  updateSelected={handleRoleChange}
                  placeholder="Select Role"
                  hideClearSelection
                  updateSelectedOnEachSelection
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1">Plan Group Tagged</Typography>
                <div style={{ opacity: editUserData.role.toLowerCase() !== 'merchant' ? 0.5 : 1, pointerEvents: editUserData.role.toLowerCase() !== 'merchant' ? 'none' : 'auto' }}>
                  <MultiSelect
                    initialData={planGroupsList}
                    selectedOptions={editUserData.planGroupTagged && editUserData.planGroupTagged.trim() !== '' ?
                      editUserData.planGroupTagged.split(', ').filter(group => group.trim() !== '').map(group => ({ value: group, label: group })) :
                      []}
                    updateSelected={handlePlanGroupChange}
                    placeholder="Select Plan Groups"
                    is_multiple_selection
                    updateSelectedOnEachSelection
                  />
                </div>
                {editUserData.role.toLowerCase() !== 'merchant' && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Plan groups can only be assigned to Merchant role
                  </Typography>
                )}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                <Button variant="outlined" onClick={onCancelClick}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={onSaveClick}>
                  Save
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={deleteModalOpen} onClose={onDeleteModalClose}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 1
        }}>
          <h4 style={{ marginTop: 0 }}>Confirm Delete</h4>
          {console.log(selectedRows)}
          {selectedRows.length === 1 && (
            <Typography variant="body1" sx={{ mb: 3 }}>
              Are you sure you want to delete the user "{selectedRows[0].data.username}"?
            </Typography>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button
              variant="contained"
              color="error"
              onClick={onDeleteUserSubmit}
            >
              Delete
            </Button>
            <Button
              variant="outlined"
              onClick={onDeleteUserCancel}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
}
